import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  ClientAccount,
  ExecutiveReportConfig,
  HorizonScan,
  ReportConfig,
  NessusScan,
  NessusScanHistory,
} from '../models';
import { AuthService } from './auth.service';
import { DemoStore } from '../stores';

@Injectable({ providedIn: 'root' })
export class AdminScanService {
  private readonly httpClient = inject(HttpClient);
  private readonly demoStore = inject(DemoStore);

  getClientAccounts(): Observable<ClientAccount[]> {
    return this.httpClient.get<ClientAccount[]>('/api/admin/modules/horizon/client-accounts');
  }

  getHorizonScans(apiKey: string, clientName: string): Observable<HorizonScan[]> {
    return this.httpClient.post<HorizonScan[]>('/api/admin/modules/horizon/scans', {
      apiKey: apiKey,
      clientName: clientName,
    });
  }

  getNessusScans(): Observable<NessusScan[]> {
    return this.httpClient.get<NessusScan[]>('/api/admin/modules/nessus/scans');
  }

  getNessusScanHistory(scanId: string): Observable<NessusScanHistory[]> {
    return this.httpClient.get<NessusScanHistory[]>(`/api/admin/modules/nessus/scans/${scanId}/history`);
  }

  generateExecutiveReport(config: ExecutiveReportConfig): Observable<Blob> {
    if (this.demoStore.isDemoMode()) {
      return of(new Blob());
    }
    return this.httpClient
      .post('/api/admin/scans/executive-report', config, {
        responseType: 'blob',
      })
      .pipe(
        tap((blob: Blob) => {
          const linkElement = document.createElement('a');
          linkElement.href = window.URL.createObjectURL(blob);
          linkElement.download = `executive-report-${config.companyName}.pdf`;
          document.body.appendChild(linkElement);
          linkElement.click();
          window.URL.revokeObjectURL(linkElement.href);
          document.body.removeChild(linkElement);
          return blob;
        }),
      );
  }

  generateReport(config: ReportConfig): Observable<Blob> {
    if (this.demoStore.isDemoMode()) {
      return of(new Blob());
    }
    return this.httpClient
      .post('/api/admin/scans/report', config, {
        responseType: 'blob',
      })
      .pipe(
        tap((blob: Blob) => {
          const linkElement = document.createElement('a');
          linkElement.href = window.URL.createObjectURL(blob);
          linkElement.download = `report-${config.scanName}-${config.companyName}.pdf`;
          document.body.appendChild(linkElement);
          linkElement.click();
          window.URL.revokeObjectURL(linkElement.href);
          document.body.removeChild(linkElement);
          return blob;
        }),
      );
  }

  generateFullReport(config: ReportConfig): Observable<Blob> {
    if (this.demoStore.isDemoMode()) {
      return of(new Blob());
    }
    return this.httpClient
      .post('/api/admin/scans/full-report', config, {
        responseType: 'blob',
      })
      .pipe(
        tap((blob: Blob) => {
          const linkElement = document.createElement('a');
          const reducedSuffix = config.reduced ? '-sea' : '';
          linkElement.href = window.URL.createObjectURL(blob);
          linkElement.download = `report-${config.scanName}-${config.companyName}${reducedSuffix}.zip`;
          document.body.appendChild(linkElement);
          linkElement.click();
          window.URL.revokeObjectURL(linkElement.href);
          document.body.removeChild(linkElement);
          return blob;
        }),
      );
  }
}
